import get from 'lodash/get';
import React, { useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { IconChevron, IconLockOutline } from '../../icon';
import { Spinner } from '../../Spinner';
import { useFieldValidation } from '../Form';
import { FormElement, FormLabel, InputDescription } from './FormCommons';

interface FormSelectProps {
    label: string;
    name: string;
    description?: string;
    vertical?: boolean;
    disabled?: boolean;
    isLoading?: boolean;
    hideLabel?: boolean;
}

export const FormSelect = ({
    label,
    children,
    name,
    description,
    disabled = false,
    isLoading = false,
    vertical = false,
    hideLabel = false,
}: PropsWithChildren<FormSelectProps>) => {
    const {
        register,
        formState: { errors },
    } = useFormContext();
    const error = get(errors, name);
    const { isRequired } = useFieldValidation(name);

    const isDisabled = isLoading || disabled;

    const icon = useMemo(() => {
        if (disabled) {
            return <IconLockOutline size="xSmall" />;
        } else if (isLoading) {
            return <Spinner size="small" />;
        }

        return <StyledIconChevron size="micro" />;
    }, [disabled, isLoading]);

    return (
        <FormElement $isVertical={vertical}>
            {!hideLabel && (
                <FormLabel>
                    {label}
                    {isRequired && '*'}
                </FormLabel>
            )}

            <SelectContainer>
                <StyledSelect disabled={isDisabled} {...register(name)}>
                    {children}
                </StyledSelect>
                <IconContainer>{icon}</IconContainer>
            </SelectContainer>
            <InputDescription errorMessage={error?.message} description={description} />
        </FormElement>
    );
};

export const Select = ({ children, ...rest }: PropsWithChildren<React.SelectHTMLAttributes<HTMLSelectElement>>) => {
    return (
        <SelectContainer>
            <StyledSelect {...rest}>{children}</StyledSelect>
            <IconContainer>
                <StyledIconChevron size="micro" />
            </IconContainer>
        </SelectContainer>
    );
};

const SelectContainer = styled.div`
    position: relative;
`;

const StyledIconChevron = styled(IconChevron)`
    transform: rotate(90deg);
`;

const IconContainer = styled.div`
    position: absolute;
    top: 50%;
    right: 9px;
    transform: translateY(-50%);
    z-index: 1;
    display: flex;
    pointer-events: none;
`;

const StyledSelect = styled.select`
    appearance: none;
    font-weight: 300;
    font-size: 1rem;

    width: 100%;
    min-width: 80px;
    height: ${({ theme }) => theme.scale.formInputHeight};
    padding: 0 32px 0 8px;

    font-family: halyard-text, sans-serif;
    color: ${({ theme }) => theme.text.primary};
    text-overflow: ellipsis;

    border-radius: 3px;
    border: 1px solid ${({ theme }) => theme.border.dark};
    cursor: pointer;

    &:disabled {
        background: ${({ theme }) => theme.intent.disabled};
        cursor: not-allowed;

        & + ${IconContainer} ${StyledIconChevron} {
            display: none;
        }
    }
`;
